// Shared helpers for the platform's location-tree pickers. The node shape
// produced by OrgLocationsAdminService.getTree is `{ id, text, items }`,
// and every picker (LocationTreePickerModal, the Kendo DropDownTree
// wrapper, and the LCP ScopePickerModal) walks it the same way to figure
// out which ancestors need to be expanded so the pre-selected node is
// visible the moment the picker opens.

// Returns the Set of ancestor ids on the path from any root to the node
// whose id === targetId (NOT including targetId itself). Empty Set when
// the id isn't found or is null/undefined.
export const findAncestorIds = (nodes, targetId) => {
  const out = new Set();
  if (targetId == null) return out;
  const walk = (level, trail) => {
    for (const n of level || []) {
      if (n?.id === targetId) {
        trail.forEach((id) => out.add(id));
        return true;
      }
      if (n?.items?.length && walk(n.items, [...trail, n.id])) return true;
    }
    return false;
  };
  walk(nodes, []);
  return out;
};

// Walk the raw org location tree ({id, text, items, isSite, isBay, ...})
// from a picked leaf back up to the root, returning an ordered array
// `[site, …, picked]` of `{id, name, isSite, isBay}`. Truncates at (and
// includes) the first ancestor flagged isSite=true so the breadcrumb
// reads "Site → … → Bay" without the org scaffolding above the site.
// Used by the /assets/add + /assets/edit General tab and the LCP
// Add-Lift Wizard to display the picked location's chain of parents
// back to its containing site.
export const buildLocationAncestry = (tree, targetId) => {
  if (targetId == null) return [];
  const path = [];
  const walk = (level, trail) => {
    for (const n of level || []) {
      const node = {
        id: n.id,
        name: n.text,
        isSite: n.isSite === true,
        isBay: n.isBay === true,
      };
      const nextTrail = [...trail, node];
      if (n.id === targetId) {
        path.push(...nextTrail);
        return true;
      }
      if (n.items?.length && walk(n.items, nextTrail)) return true;
    }
    return false;
  };
  walk(tree, []);
  if (path.length === 0) return [];
  const firstSiteIdx = path.findIndex((n) => n.isSite);
  return firstSiteIdx >= 0 ? path.slice(firstSiteIdx) : path;
};

// Returns ancestor index-path strings ("0", "0_1", ...) for every node
// that matches `predicate`. Used by Kendo TreeView consumers whose
// expand state is path-strings rather than node ids.
export const findAncestorPaths = (nodes, predicate) => {
  const out = new Set();
  const walk = (level, prefix) => {
    (level || []).forEach((n, i) => {
      const path = prefix === "" ? String(i) : `${prefix}_${i}`;
      if (predicate(n) && prefix !== "") {
        prefix.split("_").reduce((acc, part) => {
          const next = acc === "" ? part : `${acc}_${part}`;
          out.add(next);
          return next;
        }, "");
      }
      if (n?.items?.length) walk(n.items, path);
    });
  };
  walk(nodes, "");
  return Array.from(out);
};
